import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Card,
  Tabs,
  Tab,
  styled,
  Tooltip
} from '@mui/material';
import ArrowForwardTwoToneIcon from '@mui/icons-material/ArrowForwardTwoTone';
import GamezTips from '../components/GamezTips';
import GamezBites from '../components/GamezBites';
import GamezGuides from '../components/GamezGuides';
import GamezNews from '../components/GamezNews';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore from 'swiper';
import 'swiper/css';
import 'swiper/css/autoplay';
import { Autoplay } from 'swiper/modules';
import { getAllGames } from '../apiCalls/games/api_v1_games';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { AuthContext } from '../apiCalls/jwt/AuthContext';

import CallofDuty from '../static/img/home/CallOfDuty.png'
import Fortnite from '../static/img/home/Fortnite.png'
import Valorant from '../static/img/home/Valorant.png'
import Freefire from '../static/img/home/Freefire.png'
import Minecraft from '../static/img/home/Minecraft.png'
import Pubg from '../static/img/home/PUBG.png'
import TestSkillsDialog from '../components/TestSkillsDialog';
import { trackGameSelect, trackTabAttempt, trackTabSuccess, trackTabBlocked } from '../utils/analytics';

SwiperCore.use([Autoplay]);

const HeroCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '230px',
  background: '#1D1D1D',
  borderTopRightRadius: '800px',
  borderTopLeftRadius: '188px',
  overflow: 'hidden',
  '& .hero-content': {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1
  },
  '& .background-shape': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    clipPath: 'path("M 0 0 H 100% V 30% Q 85% 50% 75% 100% H 0 V 0")',
    background: 'linear-gradient(135deg, #FAAE1C 0%, #FFB938 100%)',
    zIndex: 0
  }
}));

const ContentWrapper = styled(Box)({
  position: 'relative',
  padding: '1em',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  zIndex: 2
});

const StyledTitle = styled(Typography)({
  fontFamily: "'Orbitron-SemiBold', sans-serif", 
  fontSize: '2.5rem',
  fontWeight: 'bold',
  color: '#000000',
  marginBottom: '1rem'
});

const CharacterImage = styled('div')({
  position: 'absolute',
  right: '-2%',
  top: '80%',
  transform: 'translateY(-50%)',
  height: '120%',
  width: '50%',
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right',
  zIndex: 2
});

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  '& .MuiTabs-indicator': {
    backgroundColor: '#FAAE1C',
  },
  '& .MuiTab-root': {
    fontFamily: "'Poppins-Regular', sans-serif",
    color: theme.palette.text.secondary,
    '&.Mui-selected': {
      color: '#FAAE1C',
    },
    '&.Mui-disabled': {
      opacity: 0.5,
      color: theme.palette.text.disabled,
    }
  }
}));

function Home() {
  const { auth, fetchNewRefreshToken } = useContext(AuthContext);
  const [tabValue, setTabValue] = useState(0);
  const [games, setGames] = useState([]);
  const [userSkillLevel, setUserSkillLevel] = useState('beginner');
  const [isSkillDialogOpen, setIsSkillDialogOpen] = useState(false);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useMediaQuery('(min-width:900px)');
  const isAuthenticated = Boolean(auth.accessToken);

  // Check for msisdn in URL
  useEffect(() => {
    const checkMsisdn = async () => {
      const urlParams = new URLSearchParams(location.search);
      const msisdn = urlParams.get('a');
      
      if (msisdn && !auth.accessToken) {
        try {
          await fetchNewRefreshToken(msisdn);
        } catch (error) {
          console.error("Error authenticating with msisdn:", error);
        }
      }
    };
    
    checkMsisdn();
  }, [location, auth.accessToken, fetchNewRefreshToken]);

  const getGameImage = (gameName) => {
    switch (gameName) {
      case 'Fortnite':
        return Fortnite;
      case 'PUBG':
        return Pubg;
      case 'Call of Duty':
        return CallofDuty;
      case 'Minecraft':
        return Minecraft;
      case 'Valorant':
        return Valorant;
      case 'Freefire':
        return Freefire;
      default:
        return null;
    }
  };

  useEffect(() => {
    document.title = "Home | Gamez Academy";
    const skillLevel = sessionStorage.getItem('skillLevel') || 'beginner';
    setUserSkillLevel(skillLevel);
    if (skillLevel === 'beginner') setTabValue(0);
    else if (skillLevel === 'rookie') setTabValue(1); 
    else if (skillLevel === 'pro') setTabValue(2);

    const fetchGames = async () => {
      try {
        const response = await getAllGames();
        if (response.data.success) {
          setGames(response.data.data.games);
        }
      } catch (error) {
        console.error('Error fetching games:', error);
      }
    };

    fetchGames();
  }, []);

  useEffect(() => {
    if (swiperInstance && games.length > 1) {
      swiperInstance.autoplay.start();
    }
  }, [swiperInstance, games]);

  const canAccessTab = (tabIndex) => {
    if (!isAuthenticated) {
      return tabIndex === 0;
    }
    
    const levelAccess = {
      'beginner': [0],
      'rookie': [0, 1],
      'pro': [0, 1, 2]
    };
    return levelAccess[userSkillLevel]?.includes(tabIndex) || false;
  };

  const handleTabChange = (event, newValue) => {
    const tabNames = ['beginner', 'rookie', 'pro'];
    const targetTab = tabNames[newValue];

    trackTabAttempt(targetTab, userSkillLevel, 'home');

    if (canAccessTab(newValue)) {
      setTabValue(newValue);
      trackTabSuccess(targetTab, userSkillLevel, 'home');
    } else {
      if (!isAuthenticated) {
        trackTabBlocked(targetTab, userSkillLevel, 'not_authenticated', 'home');

        const urlParams = new URLSearchParams(location.search);
        const msisdn = urlParams.get('a');
        const loginUrl = msisdn
          ? `${process.env.REACT_APP_LOGIN_URL}?a=${msisdn}`
          : process.env.REACT_APP_LOGIN_URL;

          const refferer = sessionStorage.getItem('refferer') || urlParams.get('referer') || urlParams.get('refferer');
          if(refferer == 'gamenow') {
            return;
          } else {
            // window.location.href = loginUrl;
          }
      } else {
        trackTabBlocked(targetTab, userSkillLevel, 'skill_level_required', 'home');
        setIsSkillDialogOpen(true);
      }
    }
  };

  const handleGameClick = (uuid, gameName) => {
    trackGameSelect(gameName, uuid);

    if (isAuthenticated) {
      navigate(`/game?uuid=${uuid}&game=${gameName}`);
    } else {
      const urlParams = new URLSearchParams(location.search);
      const msisdn = urlParams.get('a');
      const loginUrl = msisdn
        ? `${process.env.REACT_APP_LOGIN_URL}?a=${msisdn}`
        : process.env.REACT_APP_LOGIN_URL;
      const refferer = sessionStorage.getItem('refferer') || urlParams.get('referer') || urlParams.get('refferer');
      if(refferer == 'gamenow') {
        return;
      } else {
        // window.location.href = loginUrl;
      }
    }
  };

  const getTabProps = (index) => {
    const isAccessible = canAccessTab(index);
    return {
      disabled: false,
      label: (
        <Tooltip
          title={!isAccessible ? 
            (!isAuthenticated ? 'Login to access more content' : `Unlock this content by reaching ${index === 1 ? 'Rookie' : 'Pro'} level`) : ''}
          arrow
          placement="top"
        >
          <span>{index === 0 ? 'Beginner' : index === 1 ? 'Rookie' : 'Pro'}</span>
        </Tooltip>
      )
    };
  };

  const getContentForTab = (tabIndex) => {
    const tabToSkillLevel = {
      0: 'beginner',
      1: 'rookie',
      2: 'pro'
    };
    
    return tabToSkillLevel[tabIndex] || 'beginner';
  };

  return (
    <Box
      sx={{
        maxWidth: '100%',
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        fontFamily: "'Poppins-Regular', sans-serif",
      }}
    >
      <Box sx={{ position: 'relative', mb: 2 }}>
        <Swiper
          spaceBetween={50}
          slidesPerView={1}
          autoplay={{
            delay: 2500,
            disableOnInteraction: false,
          }}
          loop={games.length > 1}
          modules={[Autoplay]}
          onSwiper={setSwiperInstance}
          speed={800}
        >
          {games.map((game) => (
            <SwiperSlide key={game.uuid} onClick={() => handleGameClick(game.uuid, game.name)}>
              <HeroCard elevation={0}>
                <div className="background-shape" />
                <ContentWrapper>
                  <StyledTitle>{game.name}</StyledTitle>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontFamily: "'Poppins-Regular', sans-serif",
                      color: '#000000',
                      fontSize: '1.1rem',
                      maxWidth: '70%',
                    }}
                  >
                    Find the best guide on your favorite games
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Box
                      sx={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0,0,0)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <ArrowForwardTwoToneIcon color='primary' />
                    </Box>
                  </Box>
                </ContentWrapper>
                <CharacterImage
                  sx={{
                    backgroundImage: `url(${getGameImage(game.name)})`
                  }}
                />
              </HeroCard>
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>

      <StyledTabs
        value={tabValue}
        onChange={handleTabChange}
        variant="fullWidth"
        aria-label="skill level tabs"
      >
        <Tab {...getTabProps(0)} />
        <Tab {...getTabProps(1)} />
        <Tab {...getTabProps(2)} />
      </StyledTabs>

      {!isDesktop && <GamezTips skillLevel={getContentForTab(tabValue)} />}
      <GamezBites skillLevel={getContentForTab(tabValue)} />
      <GamezNews games={games} />
      <GamezGuides skillLevel={getContentForTab(tabValue)} />

      <TestSkillsDialog open={isSkillDialogOpen}
        onClose={() => setIsSkillDialogOpen(false)} />
    </Box>
  );
}

export default Home;